import type { Booking, BookingAccessory, BookingPayment } from '../api/bookings';
import { alterationTotals, recoveryLines } from '../bookings/charges';
import { depositSplit } from '../bookings/deposit';
import { formatPct } from '../bookings/discount';
import { accessoryReplacementTotal, settleBooking } from '../bookings/settlement';
import type { SettlementDeduction } from '../bookings/settlement';
import { isOffTheBooking } from '../bookings/tones';

/**
 * The money on the customer's copy, arranged so the three questions it is asked answer themselves:
 * what is owed now, what is held and coming back, and what could still be charged after the return.
 *
 * <p>
 * <strong>The document used to blend all three.</strong> Its headline `TO PAY` was
 * `balanceDue + depositDue` — rental revenue that is gone plus security that is coming back — in one
 * bold figure with nothing saying which was which; `Deposit already held` sat as a peer of the
 * balance while being excluded from that total; and post-return money had no figure at all, only the
 * sentence "Less any damage, late fee or missing accessories". Every function here exists to keep
 * one of those apart from the others.
 * </p>
 *
 * <p>
 * Pure and tested without rendering, in the shape `docket.ts` and `bookings/draft.ts` established.
 * Nothing here computes a figure the server disagrees with: the totals come off the DTO, and what is
 * added is grouping.
 * </p>
 */

/* --------------------------------------------------------------- the phases */

/**
 * Where this booking has got to, as the money is concerned.
 *
 * <p>
 * <strong>Derived from the garments, never from <c>booking.stage</c>.</strong> Partial issue is
 * ordinary — a booking sits at `InProgress` with two garments out and a third at the tailor — and
 * `stage` cannot tell "never collected" from "returned and finished": `Confirmed` covers the first,
 * `Completed` only the second, and neither survives a `Cancelled` or `Recovery` exit. `issuedOn` and
 * `returnedOn` are physical facts about garments, which is what this is really asking about.
 * </p>
 */
export type MoneyPhase = 'beforePickup' | 'outWithCustomer' | 'settled';

/** The garments this booking is actually for — the two amendment tombstones are not among them. */
export function liveGarments(booking: Booking) {
  return booking.wearers
    .flatMap((wearer) => wearer.garments)
    .filter((g) => !isOffTheBooking(g.state));
}

export function moneyPhase(booking: Booking): MoneyPhase {
  /*
    `settledOn`, not a list of states. The server stamps it in every branch of
    `SettleDepositAsync` — Refunded, Forfeited *and* PartRefunded — and the first version of this
    enumerated the states and missed the third, which is the commonest of them: some deducted, the
    rest handed back. A settled booking would have gone on printing "what you still owe".

    A test caught it before anything was rendered, which is the argument for this module existing at
    all rather than the logic living in the component.
  */
  if (booking.deposit.settledOn !== null) {
    return 'settled';
  }

  return liveGarments(booking).some((garment) => garment.issuedOn !== null)
    ? 'outWithCustomer'
    : 'beforePickup';
}

/* -------------------------------------------------------------- the charges */

export interface ChargeLine {
  label: string;
  amount: number;
  direction: 'add' | 'less';
}

export interface Receipt {
  id: number;
  label: string;
  /** Always positive; {@link givenBack} says which way it went. */
  amount: number;
  /** Rent handed back to the customer — printed as an addition to what is owed, not a payment. */
  givenBack: boolean;
}

export interface Charges {
  /** Rental, discounts, billable alteration and tax — already filtered to what this booking has. */
  lines: ChargeLine[];
  invoiceTotal: number;
  receipts: Receipt[];
  paidTotal: number;
  balance: number;
  /**
   * Whether any alteration was done free. A <em>flag</em>, not a second row: the old layout printed
   * a row labelled `Alteration` carrying the words "Included free" directly beneath a row labelled
   * `Alteration` carrying a figure, which reads as two charges. It becomes a footnote sentence.
   */
  alterationsAbsorbed: boolean;
}

export function chargeBlock(booking: Booking): Charges {
  const alterations = alterationTotals(booking);
  const discount = booking.discount;
  const lines: ChargeLine[] = [];

  const days = booking.chargedDays;

  // A cancelled or expired booking supplied nothing, so the hire is not charged and only what was
  // kept (the cancellation charge, among the recoveries below) is (2026-10-15). Until then the paper
  // listed the whole rent for a booking that never went out.
  const calledOff = booking.stage === 'Cancelled' || booking.stage === 'Expired';

  if (!calledOff) {
    lines.push({
      label: `Rental · ${days} ${days === 1 ? 'day' : 'days'}`,
      amount: booking.rentalAmount ?? 0,
      direction: 'add',
    });
  }

  /*
    Two levels, two lines, and their combined total only when both exist — one level alone is
    already its own total, and restating it would print the same figure twice under two names.

    Printed as subtractions rather than folded into the rental for the reason the old layout gave:
    a customer who was given something has to see it on the paper they take away, or the shop gets
    no credit for it and cannot answer the brother who asks for the same price next month.
  */
  if (!calledOff && discount.lineAmount) {
    lines.push({ label: 'Garment discounts', amount: discount.lineAmount, direction: 'less' });
  }

  if (!calledOff && discount.amount) {
    lines.push({ label: 'Discount on the booking', amount: discount.amount, direction: 'less' });
  }

  if (!calledOff && discount.lineAmount && discount.amount) {
    lines.push({
      label: `Total off · ${formatPct(discount.effectivePct)}%`,
      amount: discount.totalAmount,
      direction: 'less',
    });
  }

  /*
    Days the shop agreed to, named on the paper the customer takes away.

    This is the sharpest form of the rule the discount lines above follow — a charge the customer
    cannot see on their own copy is one the shop cannot answer a question about. While it was
    missing, a booking extended by six days printed "Rental · 3 days ₹4,500" and "GST ₹972" under a
    total of ₹9,072, and the ₹3,600 between them had no name anywhere on the sheet.

    Its own line rather than added into the rental above, because "Rental · 3 days" stays true: the
    slab agreed in October is not re-resolved because the customer kept the suit, and the extra days
    are charged at the line's own extension rate. Two facts, two lines.
  */
  if (!calledOff && booking.extensionAmount) {
    const days = booking.extensionDays;

    lines.push({
      label: `Kept longer · ${days} ${days === 1 ? 'day' : 'days'}`,
      amount: booking.extensionAmount,
      direction: 'add',
    });
  }

  if (!calledOff && alterations.billed > 0) {
    lines.push({ label: 'Alteration', amount: alterations.billed, direction: 'add' });
  }

  /*
    What the return assessed, on the paper the customer takes away.

    <strong>This does not contradict "damage is never given a figure".</strong> That rule is about
    the copy handed over *before* the garments go out, where the honest line is "assessed at
    inspection" because nobody has looked yet. Once a document has been raised the figure is filed,
    the customer has been charged it, and printing a total that silently contains it is the thing
    the rule exists to prevent.

    `recoveryLines` is shared with the booking's own money panel, so the two cannot word the same
    charge differently.
  */
  for (const recovery of recoveryLines(booking)) {
    lines.push({ label: recovery.label, amount: recovery.amount, direction: 'add' });
  }

  if (booking.taxAmount) {
    lines.push({ label: 'GST', amount: booking.taxAmount, direction: 'add' });
  }

  /*
    Everything but the deposit itself. A deposit receipt is money held rather than money earned, and
    listing it here would show the invoice as part-settled by a sum that is coming back.

    **The charges the deposit paid are listed too** (2026-10-14), as "From the deposit": the
    late fee and damage are on the invoice total above, and a paper that billed them and never said
    they were paid did not add up — the server's balance counts them, so this does as well. The
    same goes for a late fee taken in cash, which this list used to leave out.
  */
  const receipts = booking.payments
    .filter((payment) => payment.kind !== 'Deposit' && payment.kind !== 'DepositRefund')
    .map((payment) => ({
      id: payment.id,
      label:
        payment.amount < 0
          ? `Given back · ${payment.receiptNumber}`
          : paidFromDeposit(payment)
            ? `From the deposit · ${payment.receiptNumber}`
            : `Paid · ${payment.receiptNumber}`,
      amount: Math.abs(payment.amount),
      givenBack: payment.amount < 0,
    }));

  return {
    lines,
    invoiceTotal: booking.invoiceTotal ?? 0,
    receipts,
    paidTotal: receipts.reduce(
      (running, receipt) => running + (receipt.givenBack ? -receipt.amount : receipt.amount),
      0,
    ),
    balance: booking.balanceDue,
    alterationsAbsorbed: alterations.hasAbsorbed,
  };
}

/* -------------------------------------------------------------- the deposit */

export interface DepositBlock {
  /** Still to collect. */
  due: number;
  /** Already with the shop. */
  held: number;
  /**
   * `due + held` — the block's own subtotal, and the reason `held` is no longer rendered as a peer
   * of the invoice balance. It was previously a row in the same grid as the balance while being
   * excluded from that grid's total, which nothing on paper explained.
   */
  onHold: number;
  refundable: number;
  taken: boolean;
}

export function depositBlock(booking: Booking): DepositBlock {
  const split = depositSplit(booking.deposit);
  const { held, refundable } = split;
  // Nothing went out on a cancelled or expired booking, so there is no deposit left to take.
  const due = booking.stage === 'Cancelled' || booking.stage === 'Expired' ? 0 : split.due;

  return { due, held, onHold: due + held, refundable, taken: held > 0 };
}

/* ------------------------------------------------------------- the exposure */

export interface AccessoryExposure {
  id: number;
  name: string;
  quantity: number;
  total: number;
}

export interface ReturnExposure {
  /** Null when no rate prices a late day. A document must then say nothing rather than print zero. */
  lateFeePerDay: number | null;
  graceDays: number;
  accessories: AccessoryExposure[];
  /**
   * True when the shop is holding nothing, so every charge below is unsecured — money owed with
   * nothing to take it from. Worth saying out loud on the customer's copy rather than discovering
   * at the counter.
   */
  unsecured: boolean;
  /** Whether there is anything at all to warn about. */
  any: boolean;
}

export function returnExposure(booking: Booking): ReturnExposure {
  const accessories = liveGarments(booking)
    .flatMap((garment) => garment.accessories)
    // An unpriced accessory is one the shop absorbs; printing it at zero would read as a threat
    // with no figure behind it.
    .filter((accessory: BookingAccessory) => accessory.replacementCharge)
    .map((accessory: BookingAccessory) => ({
      id: accessory.id,
      name: accessory.name,
      quantity: accessory.quantity,

      // The one definition, shared with `settleBooking`. Before it was extracted, the terms on this
      // very document printed the unit charge while the settlement deducted the line total.
      total: accessoryReplacementTotal(accessory),
    }));

  const lateFeePerDay = booking.lateFeeTerms.perDay;

  return {
    lateFeePerDay,
    graceDays: booking.lateFeeTerms.graceDays,
    accessories,
    unsecured: depositBlock(booking).onHold <= 0,

    // Damage is always a possibility, so the block is worth printing even with no rate and no
    // priced accessory — that is the case where the only honest line is "assessed at inspection".
    any: true,
  };
}

/* -------------------------------------------------------- the settled block */

export interface SettledDeposit {
  held: number;
  /** The deductions that can be named, from `settleBooking`. */
  named: SettlementDeduction[];
  /**
   * `forfeited − sum(named)`, when positive.
   *
   * <p>
   * The late fee that was actually charged lives on a payment row, which `settleBooking` cannot see
   * — it re-derives deductions from garments and accessories. Without this remainder the itemisation
   * would not add up to the stored forfeited total and the paper would contradict the server.
   * </p>
   */
  unnamed: number;
  forfeited: number;
  /** What paid the rent still owed (2026-10-14) — neither forfeited nor given back. */
  towardsRent: number;
  refunded: number;
}

/**
 * What the settlement came to, read from what was stored rather than re-derived.
 *
 * <p>
 * <strong>The totals are the server's.</strong> `deposit.refundedAmount` and
 * `deposit.forfeitedAmount` are what the shop actually did; `settleBooking` is used only to put
 * names against the deductions. Recomputing the refund here would produce a second answer that
 * disagrees with the cash box the moment a late fee is involved.
 * </p>
 */
export function settledDeposit(booking: Booking): SettledDeposit | null {
  if (moneyPhase(booking) !== 'settled') {
    return null;
  }

  const settlement = settleBooking(booking);
  const forfeited = booking.deposit.forfeitedAmount ?? 0;
  const named = settlement.deductions;
  const namedTotal = named.reduce((running, deduction) => running + deduction.amount, 0);

  return {
    held: settlement.depositHeld,
    named,
    unnamed: Math.max(0, forfeited - namedTotal),
    forfeited,
    towardsRent: booking.deposit.appliedAmount ?? 0,
    refunded: booking.deposit.refundedAmount ?? 0,
  };
}

/**
 * What this rental cost the customer in the end: the invoice — which carries the late fee and the
 * damage too, once the settlement has billed them.
 *
 * <p>
 * <strong>The forfeited deposit is not added on top</strong> (2026-10-14). Forfeiture is how those
 * charges were paid, and since the settlement document puts them on the invoice, adding it again
 * counted every rupee of damage twice — the same double count the tax documents refuse.
 * </p>
 *
 * <p>
 * <strong>Unconditional, unlike the figure it replaces.</strong> The old `Your net cost` was
 * `balanceDue + depositDue − refundable` and was printed only when `refundable > 0` — so it vanished
 * on exactly the bookings that were finished. It was also wrong: with `depositDue === refundable`
 * the formula reduces to `balanceDue`, meaning the document labelled the customer's *remaining*
 * balance as their net cost and silently ignored everything they had already paid.
 * </p>
 */
export function totalCost(booking: Booking): number {
  return booking.invoiceTotal ?? 0;
}

/**
 * Whether a payment row is part of the deposit paying for something at settlement — an adjustment
 * the server writes with this note (`BookingService.PaidFromDepositNote`), never cash at the counter.
 */
export function paidFromDeposit(payment: Pick<BookingPayment, 'method' | 'notes'>): boolean {
  return payment.method === 'Adjustment' && payment.notes === 'Paid from the deposit';
}
